import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Heart, AlertCircle, ArrowLeft, CheckCircle2 } from "lucide-react";
import { IMAGES, CONTACT } from "../data/site";
import { fetchBranches } from "../data/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function FeedbackPage() {
  const [params] = useSearchParams();
  const slug = params.get("branch") || "";

  const [branches, setBranches] = useState([]);
  const [type, setType] = useState("appreciation");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [selectedSlug, setSelectedSlug] = useState(slug);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBranches().then(setBranches).catch(() => setBranches([]));
  }, []);

  const findBySlug = (s) => branches.find((b) => b.slug === s) || null;
  const branch = findBySlug(slug);
  const activeBranch = branch || findBySlug(selectedSlug);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const targetBranch = branch || findBySlug(selectedSlug);
    if (!targetBranch) {
      setError("Please select your branch.");
      return;
    }
    if (rating === 0) {
      setError("Please give a star rating.");
      return;
    }
    if (type === "complaint") {
      if (!name.trim()) {
        setError("Name is required when registering a complaint.");
        return;
      }
      if (!/^\d{10}$/.test(phone.trim())) {
        setError("A valid 10-digit mobile number is required for complaints.");
        return;
      }
    }
    if (!message.trim()) {
      setError("Please write your message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/feedback`, {
        branch_slug: targetBranch.slug,
        branch_name: targetBranch.name,
        type,
        rating,
        name: name.trim(),
        phone: phone.trim(),
        message: message.trim(),
      });
      setReply(data.auto_reply || "");
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141110] text-[#F8F4EC]">
      {/* header */}
      <header className="border-b border-white/10 px-6 lg:px-10 py-4">
        <div className="max-w-[820px] mx-auto flex items-center justify-between">
          <Link to="/" data-testid="feedback-home-link" className="flex items-center gap-3 group">
            <img src={IMAGES.logo} alt="SSV" className="h-10 w-10 rounded-full ring-1 ring-white/15" />
            <span className="font-marathi-bold font-extrabold text-[#F8F4EC] group-hover:text-[#F59E0B] transition-colors">
              {CONTACT.nameMarathi}
            </span>
          </Link>
          <Link to="/" className="text-sm text-[#BFAFA6] hover:text-[#F8F4EC] inline-flex items-center gap-1">
            <ArrowLeft size={15} /> Home
          </Link>
        </div>
      </header>

      <main className="px-6 lg:px-10 py-14">
        <div className="max-w-[820px] mx-auto">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="feedback-success"
              className="text-center py-16"
            >
              <CheckCircle2 size={64} className="text-[#F59E0B] mx-auto" strokeWidth={1.4} />
              <h1 className="mt-6 font-display uppercase text-4xl sm:text-5xl text-[#F8F4EC]">Thank you!</h1>
              <p className="mt-4 text-[#BFAFA6] text-lg max-w-md mx-auto">
                Your {type === "complaint" ? "complaint" : "compliment"} for{" "}
                <span className="text-[#F59E0B]">{activeBranch?.name}</span> has reached us. We
                truly value it.
              </p>

              {reply && (
                <div
                  data-testid="feedback-auto-reply"
                  className="mt-8 max-w-lg mx-auto text-left rounded-2xl border border-[#F59E0B]/30 bg-white/[0.03] p-6"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <img src={IMAGES.logo} alt="SSV" className="h-8 w-8 rounded-full ring-1 ring-white/15" />
                    <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#F59E0B]">
                      A note from Team SSV
                    </span>
                  </div>
                  <p className="text-[#F8F4EC] leading-relaxed whitespace-pre-line">{reply}</p>
                </div>
              )}

              <Link
                to="/"
                data-testid="feedback-back-home"
                className="mt-8 inline-flex px-6 py-3 rounded-full bg-[#F59E0B] text-[#141110] font-semibold hover:scale-[1.03] transition-transform"
              >
                Back to Home
              </Link>
            </motion.div>
          ) : (
            <>
              <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#F59E0B]">
                Share your experience
              </span>
              <h1 className="mt-4 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
                We’re listening
              </h1>
              {activeBranch ? (
                <p className="mt-4 text-[#BFAFA6] text-lg">
                  Branch: <span className="text-[#F8F4EC] font-semibold">{activeBranch.name}</span>
                  <span className="block text-sm mt-1">{activeBranch.address}</span>
                </p>
              ) : (
                <p className="mt-4 text-[#BFAFA6]">Please select your branch below.</p>
              )}

              <form onSubmit={submit} className="mt-10 space-y-8" data-testid="feedback-form">
                {/* branch select if no slug */}
                {!branch && (
                  <div>
                    <label className="block text-sm text-[#BFAFA6] mb-2">Select branch</label>
                    <select
                      data-testid="feedback-branch-select"
                      value={selectedSlug}
                      onChange={(e) => setSelectedSlug(e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3 text-[#F8F4EC] focus:border-[#F59E0B] outline-none"
                    >
                      <option value="">— Choose your branch —</option>
                      {branches.map((b) => (
                        <option key={b.slug} value={b.slug} className="bg-[#1F1A18]">
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* type toggle */}
                <div>
                  <label className="block text-sm text-[#BFAFA6] mb-3">Type of feedback</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      data-testid="feedback-type-appreciation"
                      onClick={() => setType("appreciation")}
                      className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl border transition-all duration-300 ${
                        type === "appreciation"
                          ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F8F4EC]"
                          : "border-white/15 text-[#BFAFA6] hover:border-white/30"
                      }`}
                    >
                      <Heart size={18} className={type === "appreciation" ? "text-[#F59E0B]" : ""} />
                      Compliment
                    </button>
                    <button
                      type="button"
                      data-testid="feedback-type-complaint"
                      onClick={() => setType("complaint")}
                      className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl border transition-all duration-300 ${
                        type === "complaint"
                          ? "border-[#E04F33] bg-[#E04F33]/10 text-[#F8F4EC]"
                          : "border-white/15 text-[#BFAFA6] hover:border-white/30"
                      }`}
                    >
                      <AlertCircle size={18} className={type === "complaint" ? "text-[#E04F33]" : ""} />
                      Complaint
                    </button>
                  </div>
                </div>

                {/* rating */}
                <div>
                  <label className="block text-sm text-[#BFAFA6] mb-3">Your rating</label>
                  <div className="flex gap-2" data-testid="feedback-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`feedback-star-${n}`}
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          size={34}
                          className={
                            (hover || rating) >= n ? "text-[#F59E0B] fill-[#F59E0B]" : "text-white/25"
                          }
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* name + phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-[#BFAFA6] mb-2">
                      Name {type === "complaint" ? <span className="text-[#e5817a]">*</span> : "(optional)"}
                    </label>
                    <input
                      data-testid="feedback-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3 text-[#F8F4EC] focus:border-[#F59E0B] outline-none"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#BFAFA6] mb-2">
                      Mobile {type === "complaint" ? <span className="text-[#e5817a]">*</span> : "(optional)"}
                    </label>
                    <input
                      data-testid="feedback-phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3 text-[#F8F4EC] focus:border-[#F59E0B] outline-none"
                      placeholder="10-digit mobile"
                    />
                  </div>
                </div>

                {/* message */}
                <div>
                  <label className="block text-sm text-[#BFAFA6] mb-2">Your message</label>
                  <textarea
                    data-testid="feedback-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    className="w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3 text-[#F8F4EC] focus:border-[#F59E0B] outline-none resize-none"
                    placeholder={
                      type === "complaint"
                        ? "Tell us what went wrong so we can fix it…"
                        : "Tell us what you loved…"
                    }
                  />
                </div>

                {error && (
                  <p data-testid="feedback-error" className="text-[#e5817a] text-sm">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="feedback-submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-4 rounded-full bg-[#F59E0B] text-[#141110] font-semibold hover:shadow-[0_0_30px_rgba(245, 158, 11,0.45)] hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit feedback"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
